import React from "react";
import { ApprovalStage, ExpenseStatus } from "../types/expense";
import { FaCheck, FaCheckCircle, FaChevronRight, FaCommentAlt, FaInfoCircle, FaReceipt, FaTimes } from "react-icons/fa";
import { Expense } from "../models/expense/Expense";
import { formatDate, formatDateTime } from "../utils/date";
import { formatNaira } from "../utils/bill";
import User from "../models/User";
import ProfileAvatar from "./ProfileAvatar";



{/* Expense List Section */ }
const statusStyles: Record<ExpenseStatus, string> = {
    draft: "bg-slate-100 text-slate-600",
    pending: "bg-amber-50 text-amber-700",
    approved: "bg-blue-50 text-blue-700",
    rejected: "bg-red-50 text-red-700",
    paid: "bg-emerald-50 text-emerald-700",
};

const statusDotStyles: Record<ExpenseStatus, string> = {
    draft: "bg-slate-400",
    pending: "bg-amber-500",
    approved: "bg-blue-500",
    rejected: "bg-red-500",
    paid: "bg-emerald-500",
};

const StatusBadge = ({ status }: { status: ExpenseStatus }) => {
    return (
        <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${statusStyles[status]}`}
        >
            <span
                className={`h-1.5 w-1.5 rounded-full ${statusDotStyles[status]}`}
            />
            {status}
        </span>
    );
};



type Approver = {
    firstName?: string;
    lastName?: string;
    portfolio?: string | null;
    phone: string;
    email?: string;
    remark: string | null;
    status: string;
};

export const ApprovalProgress = ({
    apporvers = [],
}: {
    apporvers: Approver[];
}) => {
    const sortedApprovers = apporvers

    const allApproved =
        sortedApprovers.length > 0 &&
        sortedApprovers.every((app) => app.status === "approved");

    const rejectedIndex = sortedApprovers.findIndex(
        (app) => app.status === "rejected"
    );

    const currentIndex = sortedApprovers.findIndex(
        (app) => app.status !== "approved"
    );

    const stages: Approver[] = [
        ...sortedApprovers,
        {
            firstName: "Completed",
            portfolio: "Completed",
            phone: "",
            remark: null,
            status: allApproved ? "approved" : "pending",
        },
    ];

    return (
        <div className="flex items-center">
            {stages.map((stage, index) => {
                const isFinal = index === stages.length - 1;
                const rejected = stage.status === "rejected";
                const completed =
                    rejectedIndex === -1 &&
                    (index < currentIndex || (isFinal && allApproved));

                const current =
                    !isFinal &&
                    index === currentIndex &&
                    !rejected;

                const portfolio = stage.portfolio?.toLocaleLowerCase()?.trim();
                const email = stage.email;

                const md = "managing director";

                const role = portfolio === md
                    ? "MD" : email === "finance@chrome-architecture.com"
                        ? "Admin" : portfolio;

                const label =
                    role ||
                    stage.firstName ||
                    `Stage ${index + 1}`;

                return (
                    <React.Fragment key={`${label}-${index}`}>
                        <div className="flex flex-col items-center">
                            <div
                                className={`flex h-6 w-6 items-center justify-center rounded-full text-[9px] font-bold ${rejected
                                    ? "bg-red-600 text-white"
                                    : completed
                                        ? "bg-blue-600 text-white"
                                        : current
                                            ? "border-2 border-blue-600 bg-white text-blue-600"
                                            : "border border-slate-200 bg-white text-slate-400"
                                    }`}
                            >
                                {rejected ? (
                                    <span>✕</span>
                                ) : completed ? (
                                    FaCheckCircle({
                                        size: 10,
                                        className: "text-white",
                                    })
                                ) : (
                                    index + 1
                                )}
                            </div>

                            <span
                                className={`mt-1 text-[9px] uppercase ${rejected
                                    ? "font-semibold text-red-600"
                                    : completed || current
                                        ? "font-semibold text-blue-600"
                                        : "text-slate-400"
                                    }`}
                            >
                                {label}
                            </span>
                        </div>

                        {index < stages.length - 1 && (
                            <div
                                className={`mb-4 h-px w-7 ${rejected
                                    ? "bg-red-500"
                                    : completed
                                        ? "bg-blue-500"
                                        : "bg-slate-200"
                                    }`}
                            />
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
};

export const SummaryCard = ({
    title,
    value,
    subtitle,
    icon,
    iconClass,
}: {
    title: string;
    value: string;
    subtitle: string;
    icon: React.ReactNode;
    iconClass: string;
}) => {
    return (
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between">
                <div>
                    <p className="text-xs font-medium text-slate-500">{title}</p>

                    <p className="mt-1 text-xl font-bold text-slate-800">
                        {value}
                    </p>

                    <p className="mt-1 text-[11px] text-slate-400">
                        {subtitle}
                    </p>
                </div>

                <div
                    className={`flex h-9 w-9 items-center justify-center rounded-lg ${iconClass}`}
                >
                    {icon}
                </div>
            </div>
        </div>
    );
};

export const ExpenseTableRow = ({
    expense,
    approvers,
    requestedBy,
    onClick,
}: {
    expense: Expense;
    approvers: Approver[];
    requestedBy: User;
    onClick: () => void;
}) => {
    return (
        <tr
            onClick={onClick}
            className="cursor-pointer border-t border-slate-300 transition hover:bg-blue-50/40"
        >
            <td className="px-5 py-4">
                <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                        <ProfileAvatar
                            photoUrl={requestedBy?.photoUrl}
                            firstName={requestedBy?.firstName}
                            className="border-2 border-white shadow-sm"
                        />
                    </div>

                    <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-slate-700">
                            {expense.title}
                        </p>

                        <p className="mt-0.5 text-[11px] text-slate-400">
                            {requestedBy.firstName} {requestedBy.lastName}
                        </p>
                    </div>
                </div>
            </td>

            <td className="px-5 py-4">
                <span className="rounded-md bg-slate-100 px-2 py-1 text-[11px] font-medium text-slate-600">
                    {expense.category}
                </span>
            </td>

            <td className="px-5 py-4">
                <p className="text-sm font-semibold text-slate-700">
                    {formatNaira(expense.requestedAmount)}
                </p>
            </td>

            <td className="px-5 py-4">
                <p className="text-xs text-slate-600">
                    {formatDate(expense.createdAt)}
                </p>
            </td>

            <td className="px-5 py-4">
                <ApprovalProgress apporvers={approvers} />
            </td>

            <td className="px-5 py-4">
                {/* <div className="flex items-center justify-between gap-3">
          <StatusBadge status={expense.status} /> */}

                <div className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-300">
                    {FaChevronRight({
                        size: 11,
                    })}
                </div>
                {/* </div> */}
            </td>
        </tr>
    );
};

export const ExpenseMobileCard = ({
    expense,
    approvers = [],
    requestedBy,
    onClick,
}: {
    expense: Expense;
    approvers: Approver[];
    requestedBy: User;
    onClick: () => void;
}) => {
    return (
        <button
            type="button"
            onClick={onClick}
            className="w-full border-b border-slate-400 p-4 text-left transition hover:bg-blue-50/30"
        >
            {/* Header */}
            <div className="flex items-start justify-between">
                <div className="flex min-w-0 items-center gap-3">
                    {/* Icon */}
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50">
                        <ProfileAvatar
                            photoUrl={requestedBy?.photoUrl}
                            firstName={requestedBy?.firstName}
                            className="border-2 border-white shadow-sm"
                        />
                    </div>

                    {/* Title & Reference */}
                    <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-slate-700">
                            {expense.title}
                        </p>

                        <p className="mt-0.5 truncate text-[11px] text-slate-400">
                            {requestedBy.firstName} {requestedBy.lastName}
                        </p>
                    </div>
                </div>

                {/* Status + Chevron */}
                <div className="flex shrink-0 items-center gap-2">
                    {/* <StatusBadge status={expense.status} /> */}

                    <div className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-300">
                        {FaChevronRight({
                            size: 11,
                        })}
                    </div>
                </div>
            </div>

            {/* Expense Details */}
            <div className="mt-4 grid grid-cols-2 gap-3">
                {/* Amount */}
                <div>
                    <p className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
                        Amount
                    </p>

                    <p className="mt-1 text-sm font-bold text-slate-700">
                        {formatNaira(expense.requestedAmount)}
                    </p>
                </div>

                {/* Date */}
                <div>
                    <p className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
                        Date
                    </p>

                    <p className="mt-1 text-xs font-medium text-slate-600">
                        {formatDate(expense.createdAt)}
                    </p>
                </div>

                {/* Approval Progress */}
                <div className="col-span-2 pt-1">
                    <p className="mb-2 text-[10px] font-medium uppercase tracking-wide text-slate-400">
                        Approval Progress
                    </p>

                    <ApprovalProgress apporvers={approvers} />
                </div>
            </div>
        </button>
    );
};


{/* Expense Details Section */ }
export const getStatusLabel = (status: ExpenseStatus) => {
    switch (status) {
        case "draft":
            return "Draft";
        case "pending":
            return "Pending Approval";
        case "approved":
            return "Approved";
        case "rejected":
            return "Rejected";
        case "paid":
            return "Paid";
        default:
            return status;
    }
};

export const getStatusClasses = (status: ExpenseStatus) => {
    switch (status) {
        case "approved":
        case "paid":
            return "bg-emerald-50 text-emerald-700 border-emerald-100";

        case "rejected":
            return "bg-red-50 text-red-700 border-red-100";

        case "draft":
            return "bg-slate-100 text-slate-600 border-slate-200";

        default:
            return "bg-amber-50 text-amber-700 border-amber-100";
    }
};



interface RejectExpenseModalProps {
    isOpen: boolean;
    expense: {
        title: string;
        requestedAmount: number;
        reference?: string;
    };
    rejectionReason: string;
    processing: boolean;
    formatMoney: (amount: number) => string;
    onReasonChange: (reason: string) => void;
    onCancel: () => void;
    onReject: () => void;
}

export const RejectExpenseModal: React.FC<RejectExpenseModalProps> = ({
    isOpen,
    expense,
    rejectionReason,
    processing,
    formatMoney,
    onReasonChange,
    onCancel,
    onReject,
}) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
            <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl">

                {/* Header */}
                <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
                    <div>
                        <h2 className="text-sm font-bold text-slate-800">
                            Reject Expense Request
                        </h2>

                        {expense.reference && (
                            <p className="mt-1 text-[11px] text-slate-400">
                                {expense.reference}
                            </p>
                        )}
                    </div>

                    <button
                        type="button"
                        onClick={onCancel}
                        aria-label="Close modal"
                        className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                    >
                        {FaTimes({})}
                    </button>
                </div>

                {/* Content */}
                <div className="space-y-5 p-5">

                    {/* Warning */}
                    <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3">
                        <div className="flex gap-3">
                            <div className="mt-0.5 text-red-500">
                                {FaInfoCircle({ size: 13 })}
                            </div>

                            <div>
                                <p className="text-xs font-semibold text-red-700">
                                    You are about to reject this expense request.
                                </p>

                                <p className="mt-1 text-[10px] leading-5 text-red-600">
                                    The employee will be able to see that the request
                                    was rejected and the reason provided.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Expense Summary */}
                    <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                        <div className="flex items-center justify-between gap-4">
                            <div>
                                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                                    Expense
                                </p>

                                <p className="mt-1 text-sm font-semibold text-slate-700">
                                    {expense.title}
                                </p>
                            </div>

                            <div className="text-right">
                                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                                    Requested
                                </p>

                                <p className="mt-1 text-sm font-bold text-slate-800">
                                    {formatMoney(expense.requestedAmount)}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Rejection Reason */}
                    <div>
                        <label className="mb-1.5 block text-xs font-semibold text-slate-600">
                            Rejection Reason
                            <span className="ml-1 text-red-500">*</span>
                        </label>

                        <textarea
                            rows={5}
                            value={rejectionReason}
                            onChange={(e) => onReasonChange(e.target.value)}
                            placeholder="Explain why this expense request is being rejected..."
                            className="w-full resize-none rounded-xl border border-slate-200 px-3 py-3 text-xs text-slate-700 outline-none transition placeholder:text-slate-300 focus:border-red-400 focus:ring-4 focus:ring-red-50"
                        />

                        <p className="mt-1.5 text-[10px] text-slate-400">
                            This reason will be recorded in the expense approval history.
                        </p>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex flex-col-reverse gap-2 border-t border-slate-100 bg-slate-50 px-5 py-4 sm:flex-row sm:justify-end">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="h-10 rounded-xl border border-slate-200 bg-white px-4 text-xs font-semibold text-slate-600 transition hover:bg-slate-100"
                    >
                        Cancel
                    </button>

                    <button
                        type="button"
                        disabled={processing || !rejectionReason.trim()}
                        onClick={onReject}
                        className="inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-red-600 px-5 text-xs font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {FaTimes({ size: 11 })}
                        {processing ? "Rejecting..." : "Confirm Rejection"}
                    </button>
                </div>

            </div>
        </div>
    );
};




interface ApproveExpenseModalProps {
    isOpen: boolean;
    expense: {
        requestedAmount: number;
        reference?: string;
    };
    approvalAmount: string;
    approvalComment: string;
    reductionAmount: number;
    processing: boolean;
    formatMoney: (amount: number) => string;
    formatInputAmount: (value: string) => string;
    parseAmount: (value: string) => number;
    onAmountChange: (amount: string) => void;
    onCommentChange: (comment: string) => void;
    onCancel: () => void;
    onApprove: () => void;
}

export const ApproveExpenseModal: React.FC<ApproveExpenseModalProps> = ({
    isOpen,
    expense,
    approvalAmount,
    approvalComment,
    reductionAmount,
    processing,
    formatMoney,
    formatInputAmount,
    parseAmount,
    onAmountChange,
    onCommentChange,
    onCancel,
    onApprove,
}) => {
    if (!isOpen) return null;

    const isReduced =
        parseAmount(approvalAmount) > 0 && reductionAmount > 0;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
            <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl">

                {/* Modal Header */}
                <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
                    <div>
                        <h2 className="text-sm font-bold text-slate-800">
                            Review Expense Request
                        </h2>

                        {expense.reference && (
                            <p className="mt-1 text-[11px] text-slate-400">
                                {expense.reference}
                            </p>
                        )}
                    </div>

                    <button
                        type="button"
                        onClick={onCancel}
                        aria-label="Close modal"
                        className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                    >
                        {FaTimes({})}
                    </button>
                </div>

                {/* Modal Content */}
                <div className="space-y-5 p-5">

                    {/* Requested and Approval Amounts */}
                    <div className="grid grid-cols-2 gap-3">
                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                                Staff Requested
                            </p>

                            <p className="mt-2 text-lg font-bold text-slate-800">
                                {formatMoney(expense.requestedAmount)}
                            </p>
                        </div>

                        <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-blue-500">
                                Approval Amount
                            </p>

                            <p className="mt-2 text-lg font-bold text-blue-700">
                                {approvalAmount
                                    ? `₦${approvalAmount}`
                                    : "₦0"}
                            </p>
                        </div>
                    </div>

                    {/* Approved Amount Input */}
                    <div>
                        <label className="mb-1.5 block text-xs font-semibold text-slate-600">
                            Approved Amount
                        </label>

                        <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-400">
                                ₦
                            </span>

                            <input
                                type="text"
                                inputMode="decimal"
                                value={approvalAmount}
                                onChange={(e) =>
                                    onAmountChange(
                                        formatInputAmount(e.target.value)
                                    )
                                }
                                className="h-12 w-full rounded-xl border border-slate-200 pl-8 pr-4 text-base font-bold text-slate-800 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-50"
                            />
                        </div>
                    </div>

                    {/* Amount Reduction Notice */}
                    {isReduced && (
                        <div className="rounded-xl border border-amber-100 bg-amber-50 px-4 py-3">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-semibold text-amber-700">
                                    Amount Reduced
                                </span>

                                <span className="text-sm font-bold text-amber-700">
                                    {formatMoney(reductionAmount)}
                                </span>
                            </div>

                            <p className="mt-1 text-[10px] leading-4 text-amber-600">
                                The approved amount is lower than the amount
                                requested by the employee.
                            </p>
                        </div>
                    )}

                    {/* Approval Comment */}
                    <div>
                        <label className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-slate-600">
                            {FaCommentAlt({
                                size: 10,
                                className: "text-slate-400"
                            })}

                            Comment / Reason

                            {reductionAmount > 0 && (
                                <span className="text-red-500">*</span>
                            )}
                        </label>

                        <textarea
                            rows={4}
                            value={approvalComment}
                            onChange={(e) =>
                                onCommentChange(e.target.value)
                            }
                            placeholder={
                                reductionAmount > 0
                                    ? "Explain why the requested amount was reduced..."
                                    : "Add an approval comment..."
                            }
                            className="w-full resize-none rounded-xl border border-slate-200 px-3 py-3 text-xs text-slate-700 outline-none transition placeholder:text-slate-300 focus:border-blue-500 focus:ring-4 focus:ring-blue-50"
                        />
                    </div>
                </div>

                {/* Modal Footer */}
                <div className="flex flex-col-reverse gap-2 border-t border-slate-100 bg-slate-50 px-5 py-4 sm:flex-row sm:justify-end">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="h-10 rounded-xl border border-slate-200 bg-white px-4 text-xs font-semibold text-slate-600 transition hover:bg-slate-100"
                    >
                        Cancel
                    </button>

                    <button
                        type="button"
                        disabled={
                            processing ||
                            (reductionAmount > 0 && !approvalComment.trim())
                        }
                        onClick={onApprove}
                        className="inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 text-xs font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {FaCheck({ size: 11 })}

                        {processing
                            ? "Processing..."
                            : "Approve Request"}
                    </button>
                </div>
            </div>
        </div>
    );
};